/*
 * PARAK — Partner seller pagination
 *
 * Progressive enhancement: every card is rendered in the markup, so with JS
 * disabled the full roster is visible and the pager stays [hidden]. On boot we
 * reveal the pager and page through the cards client-side.
 *
 * Note: hiding relies on `[hidden] { display: none !important }` in
 * parak-brand.css — the card class sets display:flex, which would otherwise
 * out-specify the UA stylesheet's [hidden] rule.
 */

function initPartners(root) {
  const grid = root.querySelector('[data-parak-partners-grid]');
  const pager = root.querySelector('[data-parak-partners-pager]');
  if (!grid || !pager) return;

  const cards = [...grid.querySelectorAll('[data-parak-partner-card]')];
  const perPage = parseInt(grid.dataset.perPage, 10) || 8;
  const pageCount = Math.ceil(cards.length / perPage);
  if (pageCount < 2) return;

  const nums = pager.querySelector('[data-parak-pager-nums]');
  const prevBtn = pager.querySelector('[data-parak-pager-dir="prev"]');
  const nextBtn = pager.querySelector('[data-parak-pager-dir="next"]');
  let current = 1;

  // Compact page list: first, last, current and its neighbours, ellipsis for
  // the gaps. Keeps the row to one line on mobile no matter how many pages.
  const pageList = () => {
    const pages = new Set([1, pageCount, current, current - 1, current + 1]);
    const shown = [...pages].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const p of shown) {
      if (p - prev > 1) out.push('gap');
      out.push(p);
      prev = p;
    }
    return out;
  };

  const renderNums = () => {
    nums.replaceChildren();
    for (const item of pageList()) {
      if (item === 'gap') {
        const span = document.createElement('span');
        span.className = 'parak-pager__ellipsis';
        span.textContent = '…';
        span.setAttribute('aria-hidden', 'true');
        nums.appendChild(span);
        continue;
      }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'parak-pager__num';
      btn.textContent = item;
      btn.setAttribute('aria-label', `${pager.dataset.pageLabel || 'Page'} ${item}`);
      if (item === current) {
        btn.classList.add('parak-pager__num--on');
        btn.setAttribute('aria-current', 'page');
      }
      btn.addEventListener('click', () => go(item, true));
      nums.appendChild(btn);
    }
  };

  const go = (page, scroll) => {
    current = Math.min(pageCount, Math.max(1, page));

    for (const [i, card] of cards.entries()) {
      card.hidden = Math.floor(i / perPage) !== current - 1;
    }
    renderNums();

    prevBtn.disabled = current === 1;
    nextBtn.disabled = current === pageCount;

    if (scroll) {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  prevBtn.addEventListener('click', () => go(current - 1, true));
  nextBtn.addEventListener('click', () => go(current + 1, true));

  pager.hidden = false;
  go(1, false);
}

function boot() {
  for (const root of document.querySelectorAll('[data-parak-partners]')) {
    initPartners(root);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Theme editor: re-init when the section is re-rendered.
document.addEventListener('shopify:section:load', (event) => {
  for (const root of event.target.querySelectorAll('[data-parak-partners]')) {
    initPartners(root);
  }
});
